/**
 * Edge Rendering Service
 *
 * Orchestrates edge rendering using Strategy Pattern for path calculation
 * and routing. Implements viewport synchronization and element update tracking.
 *
 * SOLID Principles:
 * - S: Single Responsibility - Only orchestrates rendering, delegates logic
 * - O: Open/Closed - New strategies without modifying this class
 * - D: Dependency Inversion - Depends on interfaces, not implementations
 */

import type { Bound } from '@blocksuite/affine/global/gfx';
import type { GfxModel } from '@blocksuite/std/gfx';

import type { EdgeMetadata } from '../core/types/edge-definition-types';
import type { IEdgePathStrategy } from '../strategies/path/types';
import type { IEdgeRenderer } from '../strategies/renderer/types';
import type { IEdgeRoutingStrategy } from '../strategies/routing/types';
import type {
  AnchorPosition,
  EdgeSystemConfig,
  EdgeSystemState,
  EdgeType,
  EdgeVisualType,
  Point,
  StreamlainEdge,
  ViewportState,
} from '../types';
import { DEFAULT_EDGE_STATE } from '../types';
import { createEdge, getEdgesForElement } from '../utils';

/**
 * Callback for element bound retrieval
 */
export type EdgeBoundEndpoint =
  | 'source'
  | 'target'
  | 'source-container'
  | 'target-container';
export type ElementBoundGetter = (
  id: string,
  edge?: StreamlainEdge,
  endpoint?: EdgeBoundEndpoint
) => Bound | null;

/**
 * Callback for getting all canvas elements
 */
export type AllElementsGetter = () => GfxModel[];

/**
 * Edge Rendering Service
 *
 * Manages edge lifecycle, rendering, and updates.
 */
export class EdgeRenderingService {
  private readonly edges: Map<string, StreamlainEdge> = new Map();
  private readonly systemState: EdgeSystemState = { ...DEFAULT_EDGE_STATE };

  // Update batching (microtask pattern from connector-watcher)
  private readonly pendingUpdates: Set<string> = new Set();
  private pendingFlag = false;

  // Viewport state
  private currentViewport: ViewportState | null = null;

  // Mounted state
  private mounted = false;
  private hasCompletedFullRender = false;

  constructor(
    private readonly renderer: IEdgeRenderer,
    private pathStrategy: IEdgePathStrategy,
    private routingStrategy: IEdgeRoutingStrategy,
    private readonly getBound: ElementBoundGetter,
    /** Reserved for collision detection in future routing strategies */
    private readonly getAllElements: AllElementsGetter,
    private readonly pathStrategyOverrides: Partial<
      Record<EdgeType, IEdgePathStrategy>
    > = {}
  ) {}

  // ===========================================================================
  // Reserved for Future Use
  // ===========================================================================

  /**
   * Gets all canvas elements (for collision detection in future routing strategies)
   */
  protected getCanvasElements(): GfxModel[] {
    return this.getAllElements();
  }

  // ===========================================================================
  // Lifecycle
  // ===========================================================================

  /**
   * Mounts the renderer to a container element
   */
  mount(container: HTMLElement): void {
    this.renderer.mount(container);
    this.mounted = true;
    this.hasCompletedFullRender = false;
  }

  /**
   * Unmounts the renderer and cleans up
   */
  unmount(): void {
    this.renderer.unmount();
    this.edges.clear();
    this.pendingUpdates.clear();
    this.mounted = false;
    this.hasCompletedFullRender = false;
  }

  /**
   * Checks if the service is mounted
   */
  isMounted(): boolean {
    return this.mounted;
  }

  // ===========================================================================
  // Edge CRUD
  // ===========================================================================

  /**
   * Adds a new edge between two elements
   * @param sourceId - Source element ID
   * @param targetId - Target element ID
   * @param type - Edge type
   * @param options - Edge ID and optional anchor hints
   */
  addEdge(
    sourceId: string,
    targetId: string,
    type: EdgeType,
    options: {
      /** Edge ID (required for sync with EdgeSyncService) */
      id: string;
      sourceAnchor?: AnchorPosition;
      targetAnchor?: AnchorPosition;
      /** Visual type for rendering colors */
      visualType?: EdgeVisualType;
      /** Semantic metadata from edge derivation */
      metadata?: EdgeMetadata;
      sourceMarker?: StreamlainEdge['sourceMarker'];
      targetMarker?: StreamlainEdge['targetMarker'];
    }
  ): StreamlainEdge {
    const config = this.systemState[type];
    const edge = createEdge(sourceId, targetId, type, {
      id: options.id,
      visible: config.enabled,
      opacity: config.baseOpacity / 100,
      highlighted: false,
      sourceAnchor: options.sourceAnchor,
      targetAnchor: options.targetAnchor,
      visualType: options.visualType,
      metadata: options.metadata,
      sourceMarker: options.sourceMarker,
      targetMarker: options.targetMarker,
    });

    // Calculate initial path (with anchor hints)
    edge.path = this.calculatePath(sourceId, targetId, edge);

    this.edges.set(edge.id, edge);
    if (
      this.previewPendingCommit &&
      this.previewEdge &&
      this.hasMatchingPermanentEdge(this.previewEdge)
    ) {
      this.previewEdge = null;
      this.previewPendingCommit = false;
    }
    this.scheduleRender();

    return edge;
  }

  /**
   * Removes an edge by ID
   */
  removeEdge(edgeId: string): void {
    if (this.edges.delete(edgeId)) {
      this.renderer.removeEdge(edgeId);
    }
  }

  /**
   * Removes all edges connected to an element
   */
  removeEdgesForElement(elementId: string): void {
    const toRemove = getEdgesForElement(this.edges, elementId);
    for (const edge of toRemove) {
      this.removeEdge(edge.id);
    }
  }

  /**
   * Gets an edge by ID
   */
  getEdge(edgeId: string): StreamlainEdge | undefined {
    return this.edges.get(edgeId);
  }

  /**
   * Gets all edges connected to an element
   */
  getEdgesForElement(elementId: string): StreamlainEdge[] {
    return getEdgesForElement(this.edges, elementId);
  }

  /**
   * Gets all edges of a specific type
   */
  getEdgesByType(type: EdgeType): StreamlainEdge[] {
    return Array.from(this.edges.values()).filter(edge => edge.type === type);
  }

  /**
   * Gets all edges
   */
  getAllEdges(): StreamlainEdge[] {
    return Array.from(this.edges.values());
  }

  // ===========================================================================
  // System Configuration
  // ===========================================================================

  /**
   * Sets whether an edge system is enabled
   */
  setSystemEnabled(type: EdgeType, enabled: boolean): void {
    this.systemState[type].enabled = enabled;

    // Update visibility of all edges of this type
    for (const edge of this.edges.values()) {
      if (edge.type === type) {
        edge.visible = enabled;
      }
    }

    this.scheduleRender();
  }

  /**
   * Sets the base opacity for an edge system
   */
  setSystemOpacity(type: EdgeType, opacity: number): void {
    this.systemState[type].baseOpacity = opacity;

    // Update opacity of all non-highlighted edges of this type
    for (const edge of this.edges.values()) {
      if (edge.type === type && !edge.highlighted) {
        edge.opacity = opacity / 100;
        this.renderer.setOpacity(edge.id, edge.opacity);
      }
    }
  }

  /**
   * Gets the current system state
   */
  getSystemState(): EdgeSystemState {
    return { ...this.systemState };
  }

  /**
   * Gets configuration for a specific system
   */
  getSystemConfig(type: EdgeType): EdgeSystemConfig {
    return { ...this.systemState[type] };
  }

  // ===========================================================================
  // Highlighting
  // ===========================================================================

  /**
   * Sets highlight state for an edge
   */
  setEdgeHighlight(edgeId: string, highlighted: boolean): void {
    const edge = this.edges.get(edgeId);
    if (!edge) return;

    edge.highlighted = highlighted;

    if (highlighted) {
      edge.opacity = 1; // Full opacity when highlighted
    } else {
      edge.opacity = this.systemState[edge.type].baseOpacity / 100;
    }

    this.renderer.setHighlight(edgeId, highlighted);
    this.renderer.setOpacity(edgeId, edge.opacity);
  }

  /**
   * Highlights all edges connected to an element
   */
  highlightEdgesForElement(elementId: string, highlighted: boolean): void {
    const edges = this.getEdgesForElement(elementId);
    for (const edge of edges) {
      this.setEdgeHighlight(edge.id, highlighted);
    }
  }

  /**
   * Clears all highlights
   */
  clearAllHighlights(): void {
    for (const edge of this.edges.values()) {
      if (edge.highlighted) {
        this.setEdgeHighlight(edge.id, false);
      }
    }
  }

  // ===========================================================================
  // Selection
  // ===========================================================================

  /**
   * Sets selection state for an edge (blue outline)
   */
  setEdgeSelected(edgeId: string, selected: boolean): void {
    const edge = this.edges.get(edgeId);
    if (!edge) return;

    edge.selected = selected;
    this.renderer.setSelected(edgeId, selected);
  }

  /**
   * Clears all selections
   */
  clearAllSelections(): void {
    for (const edge of this.edges.values()) {
      if (edge.selected) {
        this.setEdgeSelected(edge.id, false);
      }
    }
  }

  // ===========================================================================
  // Strategy Switching (Runtime)
  // ===========================================================================

  /**
   * Changes the path calculation strategy at runtime
   */
  setPathStrategy(strategy: IEdgePathStrategy): void {
    this.pathStrategy = strategy;
    this.systemState.automation.pathType = strategy.type;
    this.systemState.relation.pathType = strategy.type;
    this.updateAllEdgePaths();
  }

  /**
   * Changes the routing strategy at runtime
   */
  setRoutingStrategy(strategy: IEdgeRoutingStrategy): void {
    this.routingStrategy = strategy;
    this.systemState.automation.routingType = strategy.type;
    this.systemState.relation.routingType = strategy.type;
    this.updateAllEdgePaths();
  }

  /**
   * Gets current path strategy type
   */
  getPathStrategyType(): string {
    return this.pathStrategy.type;
  }

  /**
   * Gets current routing strategy type
   */
  getRoutingStrategyType(): string {
    return this.routingStrategy.type;
  }

  // ===========================================================================
  // Path Calculation & Updates
  // ===========================================================================

  /**
   * Calculates path between two elements using current strategy.
   * Self-loops (sourceId === targetId) use a dedicated loop path.
   *
   * @param sourceId - Source element ID
   * @param targetId - Target element ID
   * @param edge - Optional edge with anchor hints
   */
  private calculatePath(
    sourceId: string,
    targetId: string,
    edge?: StreamlainEdge
  ): string {
    const pathStrategy = edge?.type
      ? (this.pathStrategyOverrides[edge.type] ?? this.pathStrategy)
      : this.pathStrategy;
    const sourceBound = this.getBound(sourceId, edge, 'source');

    if (!sourceBound) {
      return '';
    }

    // Self-loop: source and target are the same element
    if (sourceId === targetId) {
      return pathStrategy.calculateSelfLoopPath(sourceBound);
    }

    const targetBound = this.getBound(targetId, edge, 'target');
    if (!targetBound) {
      return '';
    }

    const sourceObstacle =
      this.getBound(sourceId, edge, 'source-container') ?? sourceBound;
    const targetObstacle =
      this.getBound(targetId, edge, 'target-container') ?? targetBound;

    // Use anchor hints if available
    return pathStrategy.calculatePath(
      sourceBound,
      targetBound,
      edge?.sourceAnchor,
      edge?.targetAnchor,
      { sourceObstacle, targetObstacle }
    );
  }

  /**
   * Schedules an edge for path update (uses microtask batching)
   */
  scheduleEdgeUpdate(edgeId: string): void {
    this.pendingUpdates.add(edgeId);
    this.scheduleMicrotaskUpdate();
  }

  /**
   * Schedules updates for all edges connected to an element
   */
  scheduleEdgeUpdateForElement(elementId: string): void {
    const edges = this.getEdgesForElement(elementId);
    for (const edge of edges) {
      this.pendingUpdates.add(edge.id);
    }
    this.scheduleMicrotaskUpdate();
  }

  /**
   * Updates all edge paths (used after strategy change)
   */
  private updateAllEdgePaths(): void {
    for (const edge of this.edges.values()) {
      edge.path = this.calculatePath(edge.sourceId, edge.targetId, edge);
    }
    this.scheduleRender();
  }

  /**
   * Microtask batching for performance (pattern from connector-watcher)
   */
  private scheduleMicrotaskUpdate(): void {
    if (this.pendingFlag) return;

    this.pendingFlag = true;
    queueMicrotask(() => {
      this.processPendingUpdates();
      this.pendingFlag = false;
    });
  }

  /**
   * Processes all pending edge updates
   */
  private processPendingUpdates(): void {
    if (this.pendingUpdates.size === 0) return;

    for (const edgeId of this.pendingUpdates) {
      const edge = this.edges.get(edgeId);
      if (edge) {
        edge.path = this.calculatePath(edge.sourceId, edge.targetId, edge);
        this.renderer.updateEdge(edge);
      }
    }

    this.pendingUpdates.clear();
  }

  // ===========================================================================
  // Viewport & Rendering
  // ===========================================================================

  /**
   * Updates viewport state without recalculating model-coordinate edge paths.
   */
  updateViewport(viewport: ViewportState): void {
    this.currentViewport = viewport;

    if (!this.mounted) return;

    if (this.needsFullRenderForViewportUpdate()) {
      this.scheduleRender();
      return;
    }

    this.renderer.updateViewport(viewport);
  }

  /**
   * Triggers a full render
   * Recalculates all paths to handle node position changes
   */
  private scheduleRender(): void {
    if (!this.mounted || !this.currentViewport) {
      return;
    }

    // Recalculate ALL paths to handle node position changes (with anchor hints)
    for (const edge of this.edges.values()) {
      if (edge.visible) {
        edge.path = this.calculatePath(edge.sourceId, edge.targetId, edge);
      }
    }

    const visibleEdges = Array.from(this.edges.values()).filter(
      edge => edge.visible && edge.path
    );

    // Include preview edge if active
    if (this.previewEdge && this.previewEdge.path) {
      visibleEdges.push(this.previewEdge);
    }

    this.renderer.render(visibleEdges, this.currentViewport);
    this.hasCompletedFullRender = true;
  }

  private hasRenderableEdges(): boolean {
    return this.edges.size > 0 || this.previewEdge !== null;
  }

  private needsFullRenderForViewportUpdate(): boolean {
    if (!this.hasCompletedFullRender && this.hasRenderableEdges()) return true;

    for (const edge of this.edges.values()) {
      if (edge.visible && !edge.path) return true;
    }

    return Boolean(this.previewEdge && !this.previewEdge.path);
  }

  /**
   * Forces an immediate render
   */
  forceRender(): void {
    this.scheduleRender();
  }

  // ===========================================================================
  // Hit Testing
  // ===========================================================================

  /**
   * Gets the edge at a screen point
   */
  getEdgeAtPoint(point: Point): StreamlainEdge | null {
    return this.renderer.hitTest(point);
  }

  // ===========================================================================
  // Preview Edge (for drag-to-connect tools)
  // ===========================================================================

  private previewEdge: StreamlainEdge | null = null;
  private previewPendingCommit = false;

  /**
   * Adds a preview edge from a source element to a cursor position.
   * Used by AutomationConnectorTool during drag operations.
   *
   * @param sourceId - Source element ID
   * @param targetPoint - Cursor position in MODEL coordinates
   * @param type - Edge type (automation or relation)
   */
  addPreviewEdge(
    sourceId: string,
    targetPoint: Point,
    type: EdgeType,
    automationDirection?: 'input' | 'output'
  ): void {
    const previewMetadata = automationDirection
      ? {
          kind: 'automation' as const,
          direction: automationDirection,
          automationId:
            automationDirection === 'output' ? sourceId : '__preview__',
          databaseId:
            automationDirection === 'input' ? sourceId : '__preview__',
        }
      : undefined;
    const previewEdge = {
      id: 'preview-edge',
      sourceId,
      targetId: 'cursor',
      type,
      visible: true,
      opacity: 0.55,
      highlighted: false,
      selected: false,
      path: '',
      sourceAnchor: type === 'automation' ? ('right' as const) : undefined,
      targetAnchor: type === 'automation' ? ('left' as const) : undefined,
      metadata: previewMetadata,
      sourceMarker: type === 'automation' ? ('dot' as const) : undefined,
      targetMarker: type === 'automation' ? ('arrow' as const) : undefined,
    } satisfies StreamlainEdge;
    const sourceBound = this.getBound(sourceId, previewEdge, 'source');
    if (!sourceBound) {
      console.warn(
        '[EdgeRenderingService] Cannot add preview: source not found'
      );
      return;
    }

    // Create a virtual bound for the cursor (1x1 pixel at cursor position)
    const pathStrategy = this.pathStrategyOverrides[type] ?? this.pathStrategy;
    const targetBound = {
      x: targetPoint.x,
      y: targetPoint.y,
      w: 1,
      h: 1,
    } as Parameters<typeof this.pathStrategy.calculatePath>[1];

    // Calculate path using current strategy
    const path = pathStrategy.calculatePath(sourceBound, targetBound);

    previewEdge.path = path;
    this.previewEdge = previewEdge;
    this.previewPendingCommit = false;

    this.scheduleRender();
  }

  /**
   * Updates the preview edge target position.
   *
   * @param targetPoint - New cursor position in MODEL coordinates
   */
  updatePreviewEdge(targetPoint: Point): void {
    if (!this.previewEdge) return;

    const previewEdge = { ...this.previewEdge, targetId: 'cursor' };
    const sourceBound = this.getBound(
      previewEdge.sourceId,
      previewEdge,
      'source'
    );
    if (!sourceBound) {
      this.removePreviewEdge();
      return;
    }

    // Create virtual bound for cursor
    const targetBound = {
      x: targetPoint.x,
      y: targetPoint.y,
      w: 1,
      h: 1,
    } as Parameters<typeof this.pathStrategy.calculatePath>[1];

    // Recalculate path
    const pathStrategy =
      this.pathStrategyOverrides[previewEdge.type] ?? this.pathStrategy;
    this.previewEdge = {
      ...previewEdge,
      path: pathStrategy.calculatePath(
        sourceBound,
        targetBound,
        previewEdge.sourceAnchor,
        previewEdge.targetAnchor
      ),
    };

    this.scheduleRender();
  }

  /** Snaps the preview to a real target using the permanent edge pipeline. */
  snapPreviewEdge(targetId: string): void {
    if (!this.previewEdge) return;

    const metadata = this.previewEdge.metadata;
    const snappedEdge: StreamlainEdge = {
      ...this.previewEdge,
      targetId,
      metadata:
        metadata?.kind === 'automation'
          ? {
              ...metadata,
              automationId:
                metadata.direction === 'input'
                  ? targetId
                  : metadata.automationId,
              databaseId:
                metadata.direction === 'output'
                  ? targetId
                  : metadata.databaseId,
            }
          : metadata,
    };
    snappedEdge.path = this.calculatePath(
      snappedEdge.sourceId,
      targetId,
      snappedEdge
    );
    this.previewEdge = snappedEdge;
    this.scheduleRender();
  }

  /** Keeps the snapped preview until its derived permanent edge replaces it. */
  commitPreviewEdge(): void {
    if (!this.previewEdge || this.previewEdge.targetId === 'cursor') return;
    this.previewPendingCommit = true;
    if (this.hasMatchingPermanentEdge(this.previewEdge)) {
      this.removePreviewEdge();
    }
  }

  /** Updates preview validity through opacity without changing its geometry. */
  setPreviewValidity(isValid: boolean): void {
    if (!this.previewEdge) return;

    this.previewEdge.opacity = isValid ? 0.65 : 0.35;
    // Preview and permanent edge keep identical stroke geometry. The only
    // visual transition on commit is opacity.
    this.previewEdge.highlighted = false;

    this.scheduleRender();
  }

  /**
   * Removes the preview edge.
   */
  removePreviewEdge(): void {
    if (this.previewEdge) {
      this.previewEdge = null;
      this.previewPendingCommit = false;
      this.scheduleRender();
    }
  }

  /**
   * Checks if a preview edge is active.
   */
  hasPreviewEdge(): boolean {
    return this.previewEdge !== null;
  }

  private hasMatchingPermanentEdge(preview: StreamlainEdge): boolean {
    return Array.from(this.edges.values()).some(
      edge =>
        edge.type === preview.type &&
        edge.sourceId === preview.sourceId &&
        edge.targetId === preview.targetId
    );
  }

  // ===========================================================================
  // Factory
  // ===========================================================================

  /**
   * Creates a service with default strategies
   */
  static createDefault(
    renderer: IEdgeRenderer,
    pathStrategy: IEdgePathStrategy,
    routingStrategy: IEdgeRoutingStrategy,
    getBound: ElementBoundGetter,
    getAllElements: AllElementsGetter
  ): EdgeRenderingService {
    return new EdgeRenderingService(
      renderer,
      pathStrategy,
      routingStrategy,
      getBound,
      getAllElements
    );
  }
}
